import { ComponentType, DeviceType, EPropertyType, ESdChangeReason } from "@kuika/kuika-cl-model";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import ResponsiveDesignToolkitCustomize, { isResponsiveRowCustomizable } from "./responsive-design-toolkit-customize";

export interface IResponsiveDesignToolkitState {
  desktopType: ResponsiveTypes;
  tabletType: ResponsiveTypes;
  mobileType: ResponsiveTypes;
  selectedColIndex: number;
  responsiveRowInfo: ResponsiveRowInfo;
  isTabletDisable: boolean;
  isPhoneDisable: boolean;
}

export interface IResponsiveDesignToolkitProps {}

export enum ResponsiveTypes {
  notMatch = 0,
  notSet = 1,
  type1 = 2, // 1/1
  type2 = 3, // 1/2 + 1/2
  type3 = 4, // 1/3 + 1/3 + 1/3
  type4 = 5, // 1/4 + 1/4 + 1/4 + 1/4
  type5 = 6, // 1/6 + 1/6 + 1/6 + 1/6 + 1/6 + 1/6
  type6 = 7, // 2/3 + 1/3
  type7 = 8, // 1/4 + 2/4 + 1/4
  type8 = 9, // 1/6 + 2/6 + 2/6 + 1/6
  type9 = 10, // 1/3 + 2/3
  type10 = 11 // 1/4 + 3/4
}

// The three device sections the responsive toolkit can render. Doubles as the
// device segment of each layout option's data-testid.
type DeviceSectionName = "Desktop" | "Tablet" | "Mobile";

interface ResposiveRowData {
  cols: ResposiveColData[];
}

interface ResposiveColData {
  colIdx: number;
  responsiveColProperties: ResponsiveColProperty[];
}

export interface ResponsiveColProperty {
  deviceType: DeviceType;
  value: number;
  isSelectedColumn: boolean;
}

export interface ResponsiveRowInfo {
  desktopColumns: ResponsiveColProperty[];
  rowKuikaId: number | undefined;
}

class ResponsiveDesignToolkit extends PureSdComponent<
  IResponsiveDesignToolkitProps & IWithSDProps,
  IResponsiveDesignToolkitState
> {
  constructor(props: IResponsiveDesignToolkitProps & IWithSDProps) {
    super(props);
    this.state = {
      desktopType: ResponsiveTypes.notSet,
      tabletType: ResponsiveTypes.notSet,
      mobileType: ResponsiveTypes.notSet,
      selectedColIndex: -1,
      responsiveRowInfo: { desktopColumns: [], rowKuikaId: undefined },
      isTabletDisable: true,
      isPhoneDisable: true
    };
  }

  getSelectedDisplayItem = () => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem = this.props.sd.getSelectedDisplayItem(sd);
    return selectedDisplayItem;
  };

  getComponetDefs = () => {
    return this.props.sd.project.componentSetObj.components;
  };

  componentDidMount() {
    this.reloadData();
  }

  componentDidUpdate(
    prevProps: IResponsiveDesignToolkitProps & IWithSDProps,
    prevState: IResponsiveDesignToolkitState
  ) {
    if (this.props !== prevProps) {
      this.reloadData();
    }
  }

  reloadData = () => {
    const selectedDi = this.getSelectedDisplayItem();
    const componentDefs = this.getComponetDefs();
    if (!componentDefs || !selectedDi) {
      return;
    }
    const selectedComponentId = selectedDi.item.id;
    const component = componentDefs.find((x) => x.id === selectedComponentId);

    if (component) {
      if (component.componentType === ComponentType.responsiveColumn) {
        if (selectedDi.parentId) {
          const rowDI = this.props.sd.screenDesignDictionary[selectedDi.parentId];
          if (rowDI) {
            const selectedColIdx = rowDI.children.indexOf(selectedDi);
            this.updateSelectedResponsiveItems(rowDI.id.toString(), selectedColIdx);
          }
        }
      } else if (component.componentType === ComponentType.responsiveRow) {
        const rowDI = this.props.sd.screenDesignDictionary[selectedDi.id];
        if (rowDI) {
          this.updateSelectedResponsiveItems(rowDI.id.toString(), -1);
        }
      }
    }
  };

  handleColumnResizedByCustomize = (columnIndex: number, size: number, rightSize: number) => {
    const rowKuikaId = this.getRowDIKuikaId();
    if (rowKuikaId && rowKuikaId.length > 0) {
      this.props.sd.resizeColumns(
        rowKuikaId,
        columnIndex,
        size,
        rightSize,
        this.state.isTabletDisable,
        this.state.isPhoneDisable
      );
    }
  };

  handleColumnSplittedByCustomize = (columnIndex: number, size: number, rightSize: number) => {
    const rowKuikaId = this.getRowDIKuikaId();
    if (rowKuikaId && rowKuikaId.length > 0) {
      this.props.sd.splitColumn(
        rowKuikaId,
        columnIndex,
        size,
        rightSize,
        this.state.isTabletDisable,
        this.state.isPhoneDisable
      );
    }
  };

  getRowDIKuikaId = () => {
    const selectedDi = this.getSelectedDisplayItem();
    if (!selectedDi) {
      return undefined;
    }
    const componentDefs = this.getComponetDefs();
    const selectedComponentId = selectedDi.item.id;
    const component = componentDefs.find((x) => x.id === selectedComponentId);
    if (component) {
      if (component.componentType === ComponentType.responsiveColumn) {
        if (selectedDi.parentId) {
          const rowDI = this.props.sd.screenDesignDictionary[selectedDi.parentId];
          if (rowDI) {
            return rowDI.id.toString();
          }
        }
      } else if (component.componentType === ComponentType.responsiveRow) {
        return selectedDi.id.toString();
      }
    }
  };

  updateSelectedResponsiveItems = (rowKuikaId: string, selectedColIndex: number) => {
    // const rowDI: IDisplayItem | undefined = ScreenDesignHelper.findDisplayItemBykuikaid(
    //   rowKuikaId,
    //   this.props.sd.getScreenDesign().displayItems
    // );
    const rowDI = this.props.sd.screenDesignDictionary[parseInt(rowKuikaId, 10)];
    const responsiveRowData = { cols: [] } as ResposiveRowData;

    if (rowDI) {
      rowDI.children.forEach((colDI, colIdx) => {
        const responsiveColProperties = [] as ResponsiveColProperty[];

        const { item } = colDI;
        colDI.item.properties.forEach((p) => {
          const responsiveColProperty = {} as ResponsiveColProperty;

          const propertyDef = this.props.sd.getPropertyDefinition(item, p);
          if (propertyDef) {
            const colTypeOptions = propertyDef.typeOptions?.find(
              (x) => x.propertyType === EPropertyType.column.toString()
            );
            if (colTypeOptions?.deviceTypeForColumnAttribute) {
              responsiveColProperty.value = parseInt(
                KMainFunctions.getResponsiveColumnNumberValue(p.symbol.fixedValue)
              );
              responsiveColProperty.deviceType = colTypeOptions.deviceTypeForColumnAttribute;
            }
          }
          if (!KMainFunctions.isObjectEmpty(responsiveColProperty)) {
            responsiveColProperties.push(responsiveColProperty);
          }
        });

        const resposiveColData = {
          colIdx,
          responsiveColProperties
        } as ResposiveColData;

        responsiveRowData.cols.push(resposiveColData);
      });

      const totalColNumber = responsiveRowData.cols.length;
      const { gridSystemCount } = this.props.sd.project.componentSetObj;

      let desktopType = ResponsiveTypes.notMatch;
      let tabletType = ResponsiveTypes.notMatch;
      let mobileType = ResponsiveTypes.notMatch;

      const desktopCols = [] as ResponsiveColProperty[];
      const tabletCols = [] as ResponsiveColProperty[];
      const mobileCols = [] as ResponsiveColProperty[];
      responsiveRowData.cols.forEach((x, idx) => {
        const desktopColProperties = x.responsiveColProperties.filter((x) => x.deviceType === DeviceType.Desktop);
        if (
          desktopColProperties &&
          desktopColProperties.length > 0 &&
          desktopColProperties.length ===
            desktopColProperties.filter((x) => x.value === desktopColProperties[0].value).length
        ) {
          desktopCols.push({
            value: desktopColProperties[0].value,
            deviceType: DeviceType.Desktop,
            isSelectedColumn: selectedColIndex === idx
          });
        } else {
          desktopCols.push({
            value: -1,
            deviceType: DeviceType.Desktop,
            isSelectedColumn: selectedColIndex === idx
          });
        }

        const tabletColProperties = x.responsiveColProperties.filter((x) => x.deviceType === DeviceType.Tablet);
        if (
          tabletColProperties &&
          tabletColProperties.length > 0 &&
          tabletColProperties.length ===
            tabletColProperties.filter((x) => x.value === tabletColProperties[0].value).length
        ) {
          tabletCols.push({
            value: tabletColProperties[0].value,
            deviceType: DeviceType.Tablet,
            isSelectedColumn: selectedColIndex === idx
          });
        } else {
          tabletCols.push({
            value: -1,
            deviceType: DeviceType.Tablet,
            isSelectedColumn: selectedColIndex === idx
          });
        }

        const mobileColProperties = x.responsiveColProperties.filter((x) => x.deviceType === DeviceType.Phone);
        if (
          mobileColProperties &&
          mobileColProperties.length > 0 &&
          mobileColProperties.length ===
            mobileColProperties.filter((x) => x.value === mobileColProperties[0].value).length
        ) {
          mobileCols.push({
            value: mobileColProperties[0].value,
            deviceType: DeviceType.Phone,
            isSelectedColumn: selectedColIndex === idx
          });
        } else {
          mobileCols.push({
            value: -1,
            deviceType: DeviceType.Phone,
            isSelectedColumn: selectedColIndex === idx
          });
        }
      });

      if (totalColNumber === 1) {
        if (desktopCols.length === desktopCols.filter((x) => x.value === (1 / 1) * gridSystemCount).length) {
          desktopType = ResponsiveTypes.type1;
        }
      } else if (totalColNumber === 2) {
        if (desktopCols[0].value === (1 / 2) * gridSystemCount && desktopCols[1].value === (1 / 2) * gridSystemCount) {
          desktopType = ResponsiveTypes.type2;
        } else if (
          desktopCols[0].value === (2 / 3) * gridSystemCount &&
          desktopCols[1].value === (1 / 3) * gridSystemCount
        ) {
          desktopType = ResponsiveTypes.type6;
        } else if (
          desktopCols[0].value === (1 / 3) * gridSystemCount &&
          desktopCols[1].value === (2 / 3) * gridSystemCount
        ) {
          desktopType = ResponsiveTypes.type9;
        }
      } else if (totalColNumber === 3) {
        if (
          desktopCols[0].value === (1 / 3) * gridSystemCount &&
          desktopCols[1].value === (1 / 3) * gridSystemCount &&
          desktopCols[2].value === (1 / 3) * gridSystemCount
        ) {
          desktopType = ResponsiveTypes.type3;
        } else if (
          desktopCols[0].value === (1 / 4) * gridSystemCount &&
          desktopCols[1].value === (2 / 4) * gridSystemCount &&
          desktopCols[2].value === (1 / 4) * gridSystemCount
        ) {
          desktopType = ResponsiveTypes.type7;
        }
      } else if (totalColNumber === 4) {
        if (
          desktopCols[0].value === (1 / 4) * gridSystemCount &&
          desktopCols[1].value === (1 / 4) * gridSystemCount &&
          desktopCols[2].value === (1 / 4) * gridSystemCount &&
          desktopCols[3].value === (1 / 4) * gridSystemCount
        ) {
          desktopType = ResponsiveTypes.type4;
        } else if (
          desktopCols[0].value === (1 / 6) * gridSystemCount &&
          desktopCols[1].value === (2 / 6) * gridSystemCount &&
          desktopCols[2].value === (2 / 6) * gridSystemCount &&
          desktopCols[3].value === (1 / 6) * gridSystemCount
        ) {
          desktopType = ResponsiveTypes.type8;
        }
      } else if (totalColNumber === 6) {
        if (
          desktopCols[0].value === (1 / 6) * gridSystemCount &&
          desktopCols[1].value === (1 / 6) * gridSystemCount &&
          desktopCols[2].value === (1 / 6) * gridSystemCount &&
          desktopCols[3].value === (1 / 6) * gridSystemCount &&
          desktopCols[4].value === (1 / 6) * gridSystemCount &&
          desktopCols[5].value === (1 / 6) * gridSystemCount
        ) {
          desktopType = ResponsiveTypes.type5;
        }
      }

      if (tabletCols.length === tabletCols.filter((x) => x.value === (1 / 1) * gridSystemCount).length) {
        tabletType = ResponsiveTypes.type1;
      } else if (tabletCols.length === tabletCols.filter((x) => x.value === (1 / 2) * gridSystemCount).length) {
        tabletType = ResponsiveTypes.type2;
      } else if (tabletCols.length === tabletCols.filter((x) => x.value === (1 / 3) * gridSystemCount).length) {
        tabletType = ResponsiveTypes.type3;
      } else if (tabletCols.length === tabletCols.filter((x) => x.value === (1 / 4) * gridSystemCount).length) {
        tabletType = ResponsiveTypes.type4;
      }

      if (mobileCols.length === mobileCols.filter((x) => x.value === (1 / 1) * gridSystemCount).length) {
        mobileType = ResponsiveTypes.type1;
      } else if (mobileCols.length === mobileCols.filter((x) => x.value === (1 / 2) * gridSystemCount).length) {
        mobileType = ResponsiveTypes.type2;
      } else if (mobileCols.length === mobileCols.filter((x) => x.value === (1 / 3) * gridSystemCount).length) {
        mobileType = ResponsiveTypes.type3;
      } else if (mobileCols.length === mobileCols.filter((x) => x.value === (1 / 4) * gridSystemCount).length) {
        mobileType = ResponsiveTypes.type4;
      }

      const responsiveRowInfo: ResponsiveRowInfo = {
        rowKuikaId: this.props.sd.componentChildrenSettingsMainDi?.id,
        desktopColumns: desktopCols
      };

      const isTabletDisable = desktopType === tabletType || tabletType === ResponsiveTypes.notMatch;
      const isPhoneDisable = desktopType === mobileType || mobileType === ResponsiveTypes.notMatch;

      this.setState({
        desktopType,
        tabletType,
        mobileType,
        responsiveRowInfo,
        selectedColIndex,
        isTabletDisable,
        isPhoneDisable
      });
    }
  };

  isDIRowOrColComponent = (): boolean => {
    const selectedDi = this.getSelectedDisplayItem();
    if (!selectedDi) {
      return false;
    }
    const componentDefs = this.getComponetDefs();
    const selectedComponentId = selectedDi.item.id;
    const component = componentDefs.find((x) => x.id === selectedComponentId);
    if (component) {
      if (
        component.componentType === ComponentType.responsiveRow ||
        component.componentType === ComponentType.responsiveColumn
      )
        return true;
      // if (component.componentType === ComponentType.responsiveRow) return true;
    }
    return false;
  };

  isComponentHasDeviceTypeProperty = (deviceType: DeviceType): boolean => {
    if (this.isDIRowOrColComponent() === false) return false;
    const componentDefs = this.getComponetDefs();
    const colComponent = componentDefs.find((x) => x.componentType === ComponentType.responsiveColumn);
    let result = false;
    if (colComponent) {
      colComponent.properties?.forEach((x) => {
        return x.typeOptions?.forEach((y) => {
          if (y.deviceTypeForColumnAttribute === deviceType) {
            result = true;
          }
        });
      });
    }

    return result;
  };

  setColPropertiesForSmallSizes = (type: ResponsiveTypes, deviceType: DeviceType) => {
    const selectedDi = this.getSelectedDisplayItem();
    if (!selectedDi) {
      return;
    }
    const componentDefs = this.getComponetDefs();
    const selectedComponentId = selectedDi.item.id;
    const component = componentDefs.find((x) => x.id === selectedComponentId);
    if (component) {
      if (component.componentType === ComponentType.responsiveColumn && selectedDi.parentId) {
        this.props.sd.setColPropertiesForSmallSizes(
          type,
          selectedDi.parentId.toString(),
          deviceType,
          this.state.isTabletDisable,
          this.state.isPhoneDisable
        );
      } else if (component.componentType === ComponentType.responsiveRow) {
        this.props.sd.setColPropertiesForSmallSizes(
          type,
          selectedDi.id.toString(),
          deviceType,
          this.state.isTabletDisable,
          this.state.isPhoneDisable
        );
      }
    }
  };

  setResponsiveSettingsForDeviceType = (deviceType: DeviceType, arr: number[]) => {
    const selectedDi = this.getSelectedDisplayItem();
    if (!selectedDi) {
      return;
    }
    const componentDefs = this.getComponetDefs();
    const selectedComponentId = selectedDi.item.id;
    const component = componentDefs.find((x) => x.id === selectedComponentId);
    if (component) {
      if (component.componentType === ComponentType.responsiveColumn) {
        if (selectedDi.parentId) {
          // const rowDI: IDisplayItem | undefined = ScreenDesignHelper.findDisplayItemBykuikaid(
          //   selectedDi.parentId.toString(),
          //   this.props.sd.getScreenDesign().displayItems
          // );
          const rowDI = this.props.sd.screenDesignDictionary[selectedDi.parentId];
          if (rowDI) {
            const indexOfCol = rowDI.children.indexOf(selectedDi);

            this.props.sd.setResponsiveRowColumnSettings(
              arr,
              selectedDi.parentId.toString(),
              deviceType,
              this.state.isTabletDisable,
              this.state.isPhoneDisable
            );
            if (indexOfCol >= arr.length) {
              this.props.sd.setSelectedDisplayItem(rowDI.id.toString());
            }
          }
        }
      } else if (component.componentType === ComponentType.responsiveRow) {
        this.props.sd.setResponsiveRowColumnSettings(
          arr,
          selectedDi.id.toString(),
          deviceType,
          this.state.isTabletDisable,
          this.state.isPhoneDisable
        );
      }
    }
  };

  handleTabletTypeClick = (type: ResponsiveTypes) => {
    if (type === this.state.tabletType && this.state.isTabletDisable === false) {
      this.setState({ isTabletDisable: true, tabletType: ResponsiveTypes.notSet }, () => {
        this.resetColPropsForDesktopSettings();
      });
    } else {
      this.setState({ isTabletDisable: false }, () => {
        this.setColPropertiesForSmallSizes(type, DeviceType.Tablet);
      });
    }
  };

  resetColPropsForDesktopSettings = () => {
    const selectedDi = this.getSelectedDisplayItem();
    if (!selectedDi) {
      return;
    }
    const arr = this.getCurrentDesktopArr();
    const componentDefs = this.getComponetDefs();
    const component = componentDefs.find((x) => x.id === selectedDi.item.id.toString());
    if (component) {
      if (component.componentType === ComponentType.responsiveColumn && selectedDi.parentId) {
        this.props.sd.setResponsiveRowColumnSettings(
          arr,
          selectedDi.parentId.toString(),
          DeviceType.Desktop,
          this.state.isTabletDisable,
          this.state.isPhoneDisable
        );
      } else {
        this.props.sd.setResponsiveRowColumnSettings(
          arr,
          selectedDi.id.toString(),
          DeviceType.Desktop,
          this.state.isTabletDisable,
          this.state.isPhoneDisable
        );
      }
    }
  };

  getCurrentDesktopArr = (): number[] => {
    const res = [] as number[];
    const { gridSystemCount } = this.props.sd.project.componentSetObj;

    this.state.responsiveRowInfo.desktopColumns.forEach((x) => {
      res.push(x.value / gridSystemCount);
    });

    return res;
  };

  handleTabletSwitchChange = () => {
    const newVal = !this.state.isTabletDisable;

    if (newVal === true) {
      this.handleTabletTypeClick(this.state.tabletType);
    } else {
      this.handleTabletTypeClick(ResponsiveTypes.type1);
    }
  };

  handlePhoneTypeClick = (type: ResponsiveTypes) => {
    if (type === this.state.mobileType && this.state.isPhoneDisable === false) {
      this.setState({ isPhoneDisable: true, mobileType: ResponsiveTypes.notSet }, () => {
        this.resetColPropsForDesktopSettings();
      });
    } else {
      this.setState({ isPhoneDisable: false }, () => {
        this.setColPropertiesForSmallSizes(type, DeviceType.Phone);
      });
    }
  };

  handlePhoneSwitchChange = () => {
    const newVal = !this.state.isPhoneDisable;

    if (newVal === true) {
      this.handlePhoneTypeClick(this.state.mobileType);
    } else {
      this.handlePhoneTypeClick(ResponsiveTypes.type1);
    }
  };

  // Tokens, not literals: the preset tiles sit on the panel, so they have to flip with it.
  getLayoutColumnFill = (isActiveLayout: boolean, colIndex: number | undefined): string => {
    if (!isActiveLayout) {
      return "var(--dark-4)";
    }
    if (colIndex !== undefined && this.state.selectedColIndex === colIndex) {
      return "var(--blue-5)";
    }
    return "var(--blue-3)";
  };

  renderLayoutIcon = (
    type: ResponsiveTypes,
    currentType: ResponsiveTypes,
    rects: Array<{
      width: number;
      height: number;
      x: number;
      y: number;
      colIndex?: number;
    }>
  ) => {
    const isActive = type === currentType;
    const isSelectedCol = this.state.selectedColIndex !== -1;
    const svgClassName = `kuika_responsiveSettingsIcon ${
      isActive ? (isSelectedCol ? "active-selected" : "active") : ""
    }`;

    return (
      <svg className={svgClassName} xmlns="http://www.w3.org/2000/svg" width="36" height="24" viewBox="0 0 36 24">
        <g fill="none" fillRule="evenodd">
          <g>
            <g transform="translate(0 0)">
              <rect width="36" height="24" fill="var(--dark-1)" rx="2" />
              {rects.map((rect, index) => (
                <rect
                  key={index}
                  width={rect.width}
                  height={rect.height}
                  x={rect.x}
                  y={rect.y}
                  fill={this.getLayoutColumnFill(isActive, rect.colIndex)}
                  rx="1"
                />
              ))}
            </g>
          </g>
        </g>
      </svg>
    );
  };

  renderLayoutItem = (
    deviceType: DeviceSectionName,
    type: ResponsiveTypes,
    currentType: ResponsiveTypes,
    onClick: () => void,
    rects: Array<{
      width: number;
      height: number;
      x: number;
      y: number;
      colIndex?: number;
    }>
  ) => (
    // The same layout (e.g. type1 = a single full-width column) is offered once per
    // device section, so the test-id has to carry the device as well as the layout;
    // keyed on the enum name rather than the column count, which is not unique
    // (type2 and type6 are both two-column layouts).
    <div
      data-testid={`responsiveLayoutOption-${deviceType}-${ResponsiveTypes[type]}`}
      className="kuika_responsiveSettingsItem"
      onClick={onClick}
    >
      {this.renderLayoutIcon(type, currentType, rects)}
    </div>
  );

  renderDeviceSection = (
    deviceType: DeviceSectionName,
    deviceTypeEnum: DeviceType,
    currentType: ResponsiveTypes,
    isDisabled?: boolean,
    onSwitchChange?: () => void,
    onTypeClick?: (type: ResponsiveTypes) => void
  ) => {
    const types =
      deviceType === "Desktop"
        ? [
            { type: ResponsiveTypes.type1, cols: [1] },
            { type: ResponsiveTypes.type2, cols: [1 / 2, 1 / 2] },
            { type: ResponsiveTypes.type3, cols: [1 / 3, 1 / 3, 1 / 3] },
            { type: ResponsiveTypes.type4, cols: [1 / 4, 1 / 4, 1 / 4, 1 / 4] },
            {
              type: ResponsiveTypes.type5,
              cols: [1 / 6, 1 / 6, 1 / 6, 1 / 6, 1 / 6, 1 / 6]
            },
            { type: ResponsiveTypes.type6, cols: [2 / 3, 1 / 3] },
            { type: ResponsiveTypes.type7, cols: [1 / 4, 2 / 4, 1 / 4] },
            { type: ResponsiveTypes.type8, cols: [1 / 6, 2 / 6, 2 / 6, 1 / 6] },
            { type: ResponsiveTypes.type9, cols: [1 / 3, 2 / 3] },
            { type: ResponsiveTypes.type10, cols: [1 / 4, 3 / 4] }
          ]
        : [
            { type: ResponsiveTypes.type1, cols: [1] },
            { type: ResponsiveTypes.type2, cols: [1 / 2, 1 / 2] },
            { type: ResponsiveTypes.type3, cols: [1 / 3, 1 / 3, 1 / 3] },
            { type: ResponsiveTypes.type4, cols: [1 / 4, 1 / 4, 1 / 4, 1 / 4] }
          ];

    const layoutRects = {
      [ResponsiveTypes.type1]: [{ width: 30, height: 18, x: 3, y: 3, colIndex: 0 }],
      [ResponsiveTypes.type2]: [
        { width: 14.25, height: 18, x: 3, y: 3, colIndex: 0 },
        { width: 14.25, height: 18, x: 18.75, y: 3, colIndex: 1 }
      ],
      [ResponsiveTypes.type3]: [
        { width: 9, height: 18, x: 3, y: 3, colIndex: 0 },
        { width: 9, height: 18, x: 13.5, y: 3, colIndex: 1 },
        { width: 9, height: 18, x: 24, y: 3, colIndex: 2 }
      ],
      [ResponsiveTypes.type4]: [
        { width: 6.375, height: 18, x: 3, y: 3, colIndex: 0 },
        { width: 6.375, height: 18, x: 10.875, y: 3, colIndex: 1 },
        { width: 6.375, height: 18, x: 18.75, y: 3, colIndex: 2 },
        { width: 6.375, height: 18, x: 26.625, y: 3, colIndex: 3 }
      ],
      [ResponsiveTypes.type5]: [
        { width: 3.75, height: 18, x: 3, y: 3, colIndex: 0 },
        { width: 3.75, height: 18, x: 8.25, y: 3, colIndex: 1 },
        { width: 3.75, height: 18, x: 13.5, y: 3, colIndex: 2 },
        { width: 3.75, height: 18, x: 18.75, y: 3, colIndex: 3 },
        { width: 3.75, height: 18, x: 24, y: 3, colIndex: 4 },
        { width: 3.75, height: 18, x: 29.25, y: 3, colIndex: 5 }
      ],
      [ResponsiveTypes.type6]: [
        { width: 19.5, height: 18, x: 3, y: 3, colIndex: 0 },
        { width: 9, height: 18, x: 24, y: 3, colIndex: 1 }
      ],
      [ResponsiveTypes.type7]: [
        { width: 6.375, height: 18, x: 3, y: 3, colIndex: 0 },
        { width: 14.25, height: 18, x: 10.875, y: 3, colIndex: 1 },
        { width: 6.375, height: 18, x: 26.625, y: 3, colIndex: 2 }
      ],
      [ResponsiveTypes.type8]: [
        { width: 3.75, height: 18, x: 3, y: 3, colIndex: 0 },
        { width: 9, height: 18, x: 8.25, y: 3, colIndex: 1 },
        { width: 9, height: 18, x: 18.75, y: 3, colIndex: 2 },
        { width: 3.75, height: 18, x: 29.25, y: 3, colIndex: 3 }
      ],
      [ResponsiveTypes.type9]: [
        { width: 9, height: 18, x: 3, y: 3, colIndex: 0 },
        { width: 19.5, height: 18, x: 13.5, y: 3, colIndex: 1 }
      ],
      [ResponsiveTypes.type10]: [
        { width: 6.375, height: 18, x: 3, y: 3, colIndex: 0 },
        { width: 22.125, height: 18, x: 10.875, y: 3, colIndex: 1 }
      ]
    };

    return (
      <>
        <div className="kuika_responsiveSettingsHeader">
          <KuikaSmallMediumText>{deviceType}</KuikaSmallMediumText>
          {onSwitchChange && <KuikaSwitch checked={!isDisabled} onChange={onSwitchChange} />}
        </div>

        <div className="kuika_responsiveSettingsContent">
          {types.map(({ type, cols }) =>
            this.renderLayoutItem(
              deviceType,
              type,
              currentType,
              () => (onTypeClick ? onTypeClick(type) : this.setResponsiveSettingsForDeviceType(deviceTypeEnum, cols)),
              layoutRects[type]
            )
          )}
        </div>
      </>
    );
  };

  render() {
    return (
      <>
        {this.isDIRowOrColComponent() && (
          <div className="kuika_responsiveSettingsWrapper">
            {this.isComponentHasDeviceTypeProperty(DeviceType.Desktop) && (
              <>
                {this.renderDeviceSection("Desktop", DeviceType.Desktop, this.state.desktopType)}

                {isResponsiveRowCustomizable(
                  this.state.responsiveRowInfo,
                  this.props.sd.project.componentSetObj.gridSystemCount
                ) && (
                  <div className="kuika_responsiveSettingsCustomize">
                    <KuikaSmallMediumText>Customize</KuikaSmallMediumText>
                    <ResponsiveDesignToolkitCustomize
                      responsiveRowInfo={this.state.responsiveRowInfo}
                      gridSystemColumnCount={this.props.sd.project.componentSetObj.gridSystemCount}
                      onColumnResized={this.handleColumnResizedByCustomize}
                      onColumnSplitted={this.handleColumnSplittedByCustomize}
                    />
                  </div>
                )}
              </>
            )}

            {this.isComponentHasDeviceTypeProperty(DeviceType.Tablet) &&
              this.renderDeviceSection(
                "Tablet",
                DeviceType.Tablet,
                this.state.tabletType,
                this.state.isTabletDisable,
                this.handleTabletSwitchChange,
                this.handleTabletTypeClick
              )}

            {this.isComponentHasDeviceTypeProperty(DeviceType.Phone) &&
              this.renderDeviceSection(
                "Mobile",
                DeviceType.Phone,
                this.state.mobileType,
                this.state.isPhoneDisable,
                this.handlePhoneSwitchChange,
                this.handlePhoneTypeClick
              )}
          </div>
        )}
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangeResponsiveRowColumnSettings ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    ) {
      return true;
    }
    return false;
  };
}

const responsiveDesignToolkit = withSD<IResponsiveDesignToolkitProps>(ResponsiveDesignToolkit);
export { responsiveDesignToolkit as ResponsiveDesignToolkit };
